import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Info } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ConfigureDoorModal from "@/components/ConfigureDoorModal";
import { INTERIOR_MODELS } from "@/lib/catalog";

const FINISH_EXPLAINERS = [
  {
    id: "forma",
    name: "Forma",
    finish: "Melamine",
    blurb:
      "A decorative printed layer fused to MDF or particleboard under heat and pressure. Modern melamine convincingly resembles wood with exceptional scratch, moisture and daily-wear resistance.",
  },
  {
    id: "porto",
    name: "Porto",
    finish: "Natural Wood Veneer",
    blurb:
      "Real wood sliced into very thin sheets glued onto an engineered core. Rich grain, organic character, timeless appeal — engineered on a dimensionally stable structure.",
  },
  {
    id: "siena",
    name: "Siena",
    finish: "Painted MDF",
    blurb:
      "MDF is engineered from finely-ground wood fibers pressed into perfectly uniform panels. Its dense, seamless surface takes high-quality paint beautifully for a truly flawless painted door.",
  },
];

export default function InteriorCollectionPage() {
  const [modalModel, setModalModel] = useState(null);

  return (
    <div className="min-h-screen bg-brand-bone flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 md:pt-28 pb-16 md:pb-24">
        <section className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[0.62rem] tracking-[0.28em] uppercase text-brand-ink/60 hover:text-brand-green"
              data-testid="back-to-home"
            >
              <ArrowLeft size={13} /> Back
            </Link>
            <div className="mt-4 text-[0.72rem] tracking-[0.32em] uppercase text-brand-gold">
              Interior Collection
            </div>
            <h1 className="mt-3 font-serif text-4xl md:text-6xl text-brand-green leading-[1.02]">
              Choose your <span className="italic text-brand-gold">design</span>
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-brand-ink/75 leading-relaxed" data-testid="interior-collection-description">
              Browse our full interior door library, pick a design you love, then select a
              standard size and add it to your cart.
            </p>
          </div>

          {/* Made-to-order notice */}
          <div
            className="mb-8 md:mb-10 border-l-4 border-brand-gold bg-brand-green text-brand-bone px-5 md:px-6 py-4 md:py-5 flex items-start gap-3"
            data-testid="made-to-order-notice"
          >
            <Info size={18} className="text-brand-gold shrink-0 mt-0.5" />
            <p className="text-[0.78rem] md:text-[0.86rem] leading-relaxed">
              <span className="tracking-[0.16em] uppercase text-brand-gold font-medium mr-2">
                Made-to-order
              </span>
              The photos below are catalog examples of design language. Every door is made-to-order —
              colors and finishes are hand-selected with our atelier team once your estimate has
              been prepared.
            </p>
          </div>

          {/* Finish explainers (3 up) */}
          <div className="mb-10 md:mb-14">
            <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-gold mb-3" data-testid="interior-finishes-heading">
              Interior finishes
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5"
              data-testid="finish-explainers"
            >
              {FINISH_EXPLAINERS.map((f) => (
                <div
                  key={f.id}
                  className="border-l-2 border-brand-gold bg-brand-bone-2/60 px-5 py-4"
                  data-testid={`finish-explainer-${f.id}`}
                >
                  <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-green font-medium">
                    {f.name} · {f.finish}
                  </div>
                  <p className="mt-2 text-[0.78rem] text-brand-ink/80 leading-relaxed">
                    {f.blurb}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* General Configuration — "Design your own" card at top of grid */}
          <div className="mb-6 md:mb-8">
            <button
              type="button"
              onClick={() =>
                setModalModel({
                  id: "custom-interior",
                  is_custom: true,
                  category: "Interior",
                  name: "Design your own Interior",
                  description:
                    "Start from scratch. Pick your finish family (Forma, Porto or Siena), door type, and standard size. Our atelier team will curate the exact door design and color palette with you after we prepare your estimate.",
                  door_type: "Swing Door",
                  material: "Custom",
                  finish: "Forma",
                  hardware: "Premium Lever Handle",
                  gallery: INTERIOR_MODELS.slice(0, 6).map((m) => m.image),
                })
              }
              className="group w-full text-left flex flex-col md:flex-row items-stretch border border-brand-gold bg-brand-green text-brand-bone overflow-hidden hover:shadow-xl transition-all duration-300"
              data-testid="general-config-card-interior"
            >
              <div className="md:w-1/3 aspect-[16/9] md:aspect-auto bg-brand-charcoal flex items-center justify-center relative overflow-hidden">
                <img
                  src={INTERIOR_MODELS[0].image}
                  alt="Design your own interior"
                  className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-brand-gold text-[2.5rem] md:text-[3rem] font-serif italic">
                    +
                  </span>
                </div>
              </div>
              <div className="flex-1 px-6 md:px-8 py-6 md:py-8 flex flex-col justify-center">
                <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold">
                  General Configuration
                </div>
                <h3 className="mt-2 font-serif text-2xl md:text-3xl text-brand-bone leading-tight">
                  Design your own <span className="italic text-brand-gold">interior</span>
                </h3>
                <p className="mt-3 text-[0.85rem] md:text-sm text-brand-bone/75 leading-relaxed max-w-xl">
                  Pick your finish family (Forma, Porto or Siena), swing or sliding,
                  and standard size. We&apos;ll craft the design and palette with you
                  after the estimate.
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-[0.68rem] tracking-[0.22em] uppercase text-brand-gold group-hover:text-brand-bone transition-colors">
                  Start Designing →
                </span>
              </div>
            </button>
          </div>

          {/* Section divider — Signature Designs */}
          <div className="mb-6 md:mb-8 flex items-center gap-4">
            <span className="h-px flex-1 bg-brand-green/20" />
            <div className="text-center">
              <div className="text-[0.72rem] tracking-[0.32em] uppercase text-brand-gold font-medium">
                Signature Designs
              </div>
              <div className="mt-1 text-[0.66rem] text-brand-ink/55 italic">
                Or start from one of our house favourites
              </div>
            </div>
            <span className="h-px flex-1 bg-brand-green/20" />
          </div>

          {/* Model grid — all signature designs in one place */}
          <div
            className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
            data-testid="interior-model-grid"
          >
            {INTERIOR_MODELS.map((m, i) => (
              <motion.button
                key={m.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (i % 6) * 0.05, duration: 0.5 }}
                onClick={() => setModalModel({ ...m, category: "Interior" })}
                className="group text-left bg-white border border-brand-green/10 hover:border-brand-gold transition-all duration-300 hover:shadow-lg overflow-hidden"
                data-testid={`model-card-${m.id}`}
              >
                <div className="aspect-[3/4] bg-brand-bone-2 flex items-center justify-center overflow-hidden">
                  <img
                    src={m.image}
                    alt={m.name}
                    loading="lazy"
                    className={`w-full h-full ${m.imageFit === "contain" ? "object-contain" : "object-cover group-hover:scale-[1.03]"} transition-transform duration-700`}
                    data-testid={`model-image-${m.id}`}
                  />
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="font-serif text-lg md:text-xl text-brand-green leading-snug" data-testid={`model-name-${m.id}`}>
                    {m.name}
                  </h3>
                  <p className="mt-2 text-[0.72rem] text-brand-ink/60 line-clamp-2" data-testid={`model-description-${m.id}`}>
                    {m.description}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[0.62rem] tracking-[0.22em] uppercase text-brand-green group-hover:text-brand-gold transition-colors">
                    Configure →
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <ConfigureDoorModal
        open={!!modalModel}
        model={modalModel}
        onClose={() => setModalModel(null)}
      />
    </div>
  );
}
